import type { Alarm, ReviewFlag, Station } from '../api/types'
import { alarmCounts, alarmCountsText, alarmLink, alarmStations, alarmWhere } from './alerts'
import { type Freshness, lastObservation, MONITORING_DELAYED, statusTone, systemStatus, type SystemStatus } from './freshness'
import { checksStale, duration } from './health'
import { byProject, NO_PROJECT } from './projects'
import { filterStations, matchesStation, searchTerms } from './stationSearch'
import { formatDateTime, relativeAge } from './time'

export type OverviewCategory = 'alarms' | 'missing' | 'collection' | 'checks'
export interface OverviewIssue {
  id: string; station: string; title: string; sensor?: string; projects: string[];
  category: OverviewCategory; label: string; detail: string; lastAt: string | null;
  /** Where the issue's name leads; null for the alarm evaluation, which has
   *  no station and no page of its own (issueTarget). */
  to: string | null; tone: 'alarm' | 'warning' | 'late' | 'stale'; rank: number;
  /** A strategy's alarm: the project it is scoped by, whatever its stations' projects. */
  project?: string;
  /** An alarm's level, whether or not the rules are being checked right now. */
  level?: 'alarm' | 'warning';
}

/** What a flag is made against. A flag holds until the incident it was made
 *  for ends, and each kind of issue says what the incident is (design
 *  section 7.1): no readings ends when a reading arrives, so its occurrence is
 *  the last reading's time; a quiet channel ends when that channel reports,
 *  so its own last time; a collection problem and the summary of "more
 *  channels quiet" end with their alert, whose id is already in the issue's.
 *  An alarm ends with its event, and carries its level too: a Warning that
 *  rises to Alarm keeps its event id, and someone who flagged the Warning has
 *  not said the Alarm is known (a flag never hides a new problem). A late
 *  reading has no end of its own, so its occurrence is just its id and the
 *  server asks again after seven days. */
export function issueOccurrence(issue: OverviewIssue): string {
  if (issue.level) return `${issue.id}:${issue.level}`
  const at = issue.lastAt && Number.isFinite(Date.parse(issue.lastAt)) ? new Date(issue.lastAt).toISOString() : null
  const endsWithReading = issue.id.startsWith('missing-') || (issue.id.startsWith('quiet-') && issue.sensor !== undefined)
  return endsWithReading && at ? at : issue.id
}

/** Monitoring delayed says that checks have stopped: nobody may hide it. */
export const canFlagIssue = (issue: OverviewIssue): boolean => issue.category !== 'checks'

/** The flags that still hold for the issues shown now: the same incident
 *  (a different occurrence is a new one), and not past the server's
 *  `expires_at`. The browser never works out an expiry itself, so a flag
 *  from before occurrences existed, whose occurrence is null, hides nothing. */
export function activeIssueFlags(issues: OverviewIssue[], flags: ReviewFlag[], now: Date): Set<string> {
  const byId = new Map(issues.map(issue => [issue.id, issue]))
  return new Set(flags.filter(flag => {
    const issue = byId.get(flag.issue_key)
    return !!issue && canFlagIssue(issue) && flag.occurrence === issueOccurrence(issue)
      && (flag.expires_at === null || now.getTime() < Date.parse(flag.expires_at))
  }).map(flag => flag.issue_key))
}

/** Present existing evidence; absent checks can never establish a healthy state. */
export function overviewChecksStale(station: Station, now: Date, available = true): boolean {
  return !available || !Number.isFinite(Date.parse(station.health_checked_at ?? '')) || checksStale(station.health_checked_at, now)
}

/** `rulesKnown` is false until the first alarm request has answered, with
 *  data or an error: until then the rules are not known to be unchecked,
 *  only not yet heard from, and Monitoring delayed must not count them. */
export function overviewIssues(stations: Station[], alarms: Alarm[], now: Date, rulesCurrent: boolean, stationChecksCurrent = true,
                               rulesKnown = true): OverviewIssue[] {
  const byCode = new Map(stations.map(station => [station.code, station]))
  // A strategy's alarm (db/036) is its project's: titled by the project, led
  // to the strategy, and counted once, under the first station it reads.
  const items: OverviewIssue[] = alarms.map(alarm => ({
    id: `alarm-${alarm.id}`, station: alarmStations(alarm)[0]?.code ?? '', title: alarmWhere(alarm),
    sensor: alarm.parameter?.name,
    projects: alarm.station ? byCode.get(alarm.station.code)?.projects ?? [] : alarm.project ? [alarm.project.name] : [],
    category: 'alarms', label: `${rulesCurrent ? '' : 'Last known '}${alarm.level === 'alarm' ? 'Alarm' : 'Warning'}`,
    detail: alarm.rule.name, lastAt: alarm.since,
    to: alarmLink(alarm), tone: rulesCurrent ? alarm.level : 'stale',
    rank: alarm.level === 'alarm' ? 0 : 2, level: alarm.level,
    ...(alarm.station ? {} : { project: alarm.project?.name ?? '' }),
  }))
  for (const station of stations) {
    if (!station.active || !station.schedule_enabled) continue
    const stale = overviewChecksStale(station, now, stationChecksCurrent)
    const observation = lastObservation(station, now, stale)
    const status = systemStatus(station, stale)
    const common = { station: station.code, title: station.name, projects: station.projects ?? [],
      lastAt: observation.at, to: `/explore/${encodeURIComponent(station.code)}` }
    if (stale) {
      // Said as delayed monitoring, not as a task: nobody has to check
      // anything by hand (Tamar, PM-23).
      items.push({ ...common, lastAt: station.health_checked_at ?? null, id: `checks-${station.code}`, category: 'checks', label: 'Monitoring delayed',
        detail: 'Automatic station checks are unavailable. Refresh or contact your administrator.', tone: 'stale', rank: 1 })
      continue
    }
    if (status.status === 'no_data') {
      items.push({ ...common, id: `missing-${station.code}`, category: 'missing', label: 'No readings',
        detail: observation.at ? `Last reading ${relativeAge(observation.at, now)}` : 'No stored reading', tone: 'alarm', rank: 1 })
    } else if (observation.fresh === 'late' || observation.fresh === 'over') {
      items.push({ ...common, id: `late-${station.code}`, category: 'missing',
        label: observation.fresh === 'late' ? 'Late reading' : 'Overdue reading',
        detail: `Last reading ${relativeAge(observation.at, now)}${station.expected_interval_s ? ` · Expected every ${duration(station.expected_interval_s * 1000)}` : ''}`,
        tone: observation.fresh === 'late' ? 'late' : 'alarm', rank: observation.fresh === 'late' ? 3 : 1 })
    }
    for (const alert of station.alerts ?? []) {
      if (alert.kind === 'channels_quiet') {
        const quietIds = new Set<string>()
        for (const channel of alert.detail.channels) {
          // No alert id: `channels_quiet` closes its alert once every channel
          // is quiet, and a station outage would give the next alert a new id
          // and bring a known damaged sensor back as new.
          const id = `quiet-${station.code}-${encodeURIComponent(JSON.stringify([channel.instrument, channel.name]))}`
          if (quietIds.has(id)) continue
          quietIds.add(id)
          items.push({ ...common, id, sensor: [channel.instrument, channel.name].filter(Boolean).join(' · '),
            category: 'missing', label: 'Sensor quiet', detail: 'No readings for over a day while other channels report.',
            lastAt: channel.last_at, to: `/sensors/info/${encodeURIComponent(station.code)}`, tone: 'late', rank: 3 })
        }
        const remaining = Math.max(0, alert.detail.count - alert.detail.channels.length)
        if (remaining) items.push({ ...common, id: `quiet-${station.code}-${alert.id}-remaining`, category: 'missing', label: 'Sensors quiet',
          detail: `${remaining} additional quiet ${remaining === 1 ? 'channel' : 'channels'}; open Sensor Info to review.`,
          to: `/sensors/info/${encodeURIComponent(station.code)}`, tone: 'late', rank: 3 })
      } else if (alert.kind === 'source_failing') {
        items.push({ ...common, id: `collection-${station.code}-${alert.id}`, category: 'collection',
          label: alert.detail.gateway ? 'Gateway offline' : 'Collection failed', detail: 'The source is not responding to collection requests.', tone: 'warning', rank: 2 })
      } else if (alert.kind === 'intake_stuck') {
        items.push({ ...common, id: `collection-${station.code}-${alert.id}`, category: 'collection', label: 'Intake blocked',
          detail: 'Some arriving data has not been stored.', tone: 'alarm', rank: 1 })
      }
    }
  }
  // No link of its own: every filter lives in the address, so a link back
  // to /overview would quietly clear the reader's project, search and card.
  if (!rulesCurrent && rulesKnown) items.push({ id: 'alarm-checks', station: '', title: 'Alarm evaluation', projects: [], category: 'checks',
    label: 'Monitoring delayed', detail: 'Automatic alarm evaluation is unavailable. Refresh or contact your administrator.', lastAt: null, to: null, tone: 'stale', rank: 1 })
  return items.sort((a, b) => a.rank - b.rank || a.title.localeCompare(b.title) || (a.sensor ?? '').localeCompare(b.sensor ?? ''))
}

/** How many issues each card and notice counts. A known issue is not a new
 *  one, so it leaves the count; an alarm stays, since flagging one is not
 *  suppressing it (and its card reads the alarms themselves). */
export function categoryCounts(issues: OverviewIssue[], flags: ReadonlySet<string> = new Set()): Record<OverviewCategory, number> {
  const counts = { alarms: 0, missing: 0, collection: 0, checks: 0 }
  for (const item of issues) if (item.category === 'alarms' || !flags.has(item.id)) counts[item.category] += 1
  return counts
}

/** How many of each are flagged: what a card says beside its count. */
export function knownCounts(issues: OverviewIssue[], flags: ReadonlySet<string>): Record<OverviewCategory, number> {
  const counts = { alarms: 0, missing: 0, collection: 0, checks: 0 }
  for (const item of issues) if (flags.has(item.id)) counts[item.category] += 1
  return counts
}

/** New problems first, known ones after them, then the queue's urgency, then
 *  by name: thirty flagged sensors must not push a station that stopped an
 *  hour ago down to row 31. */
export function orderIssues(issues: OverviewIssue[], flags: Set<string>): OverviewIssue[] {
  return [...issues].sort((a, b) => Number(flags.has(a.id)) - Number(flags.has(b.id))
    || a.rank - b.rank || a.title.localeCompare(b.title))
}

export const REVIEW_FLAGS_KEY = 'hydro:overview-review-flags:v1'
export function parseReviewFlags(raw: string | null): Set<string> {
  try {
    const value: unknown = JSON.parse(raw ?? '[]')
    return new Set(Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string' && id.length > 0 && id.length <= 700).slice(0, 1000) : [])
  } catch { return new Set() }
}

// ------------------------------------------------- filters in the address

/** The two sources the station list tells apart: every Sommer station is an
 *  MRL logger, whether MDS relays it or it pushes to us; every gateway is
 *  Worldsensing. */
export type StationSource = 'mrl' | 'ws'
export const SOURCE_LABEL: Record<StationSource, string> = { mrl: 'MRL', ws: 'WS Gateway' }

export function sourceOf(station: Pick<Station, 'vendor'>): StationSource {
  return station.vendor === 'cmt_edge' ? 'ws' : 'mrl'
}

/** What the Needs attention queue shows: every issue, one card's category,
 *  or the reader's own flags. */
export type IssueFilter = 'all' | 'flagged' | OverviewCategory
const ISSUE_FILTERS: IssueFilter[] = ['flagged', 'alarms', 'missing', 'collection', 'checks']

/** Everything that narrows the page lives in the address, so a filtered view
 *  can be bookmarked or sent, and an old /stations link (App.tsx forwards it
 *  with its query) lands on the same view. `q`, `attention`, `source` and
 *  `project` are the Stations board's own names, kept so its links still
 *  mean what they said; `issue` is the card a reader picked. */
export interface OverviewFilters {
  q: string; attention: boolean; source: StationSource | null; project: string | null; issue: IssueFilter
}

export function readOverviewFilters(params: URLSearchParams): OverviewFilters {
  const source = params.get('source')
  const issue = params.get('issue')
  return {
    q: params.get('q') ?? '',
    attention: params.get('attention') === '1',
    source: source === 'mrl' || source === 'ws' ? source : null,
    // Kept as written, not checked against the reader's projects here:
    // while their list loads or fails the choice must survive a search
    // typed meanwhile (lib/projects projectFilter says what it means).
    project: params.get('project') || null,
    issue: ISSUE_FILTERS.find(known => known === issue) ?? 'all',
  }
}

/** The address for a set of filters: only what narrows, so the plain
 *  Overview stays a plain /overview. */
export function overviewFilterParams(filters: OverviewFilters): Record<string, string> {
  return {
    ...(filters.q ? { q: filters.q } : {}), ...(filters.attention ? { attention: '1' } : {}),
    ...(filters.source ? { source: filters.source } : {}), ...(filters.project ? { project: filters.project } : {}),
    ...(filters.issue !== 'all' ? { issue: filters.issue } : {}),
  }
}

// ------------------------------------------------------------- the search

/** Whether a "/" pressed anywhere on the page should put the cursor in the
 *  search box, as on most sites with one: not with a modifier held (a
 *  browser or system shortcut), and not while the reader is typing
 *  somewhere else, where "/" is a character they meant. */
export function slashFocusesSearch(key: { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean },
                                   target: { tagName: string; isContentEditable: boolean } | null): boolean {
  if (key.key !== '/' || key.ctrlKey || key.metaKey || key.altKey) return false
  return !target || !(target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
}

/** Escape in the search box: the first press clears what was typed, the
 *  next leaves the box, so a reader never loses the page's keyboard to it. */
export function escapeInSearch(query: string): 'clear' | 'leave' {
  return query ? 'clear' : 'leave'
}

// ---------------------------------------------- what the filters leave

/** Whether something at this station belongs to the chosen project, by the
 *  rule the station list follows (lib/projects byProject). `project` is the
 *  filter as projectFilter resolved it (a name, or NO_PROJECT), never the
 *  address's id: stations carry their projects by name. Something the
 *  station list does not know, the alarm evaluation itself or a station the
 *  list failed to bring, stays in view: a filter must not hide what it
 *  cannot place. */
export function inProjectScope(station: Pick<Station, 'projects'> | undefined, project: string | null): boolean {
  if (project === null || !station) return true
  const projects = station.projects ?? []
  return project === NO_PROJECT ? !projects.length : projects.includes(project)
}

/** One search for the whole page: every word must appear in the issue's own
 *  words (station, sensor, label, detail) or in its station's searchable
 *  fields, matched as the station search matches (lib/stationSearch). */
export function issueMatches(issue: OverviewIssue, station: Station | undefined, query: string): boolean {
  const terms = searchTerms(query)
  if (!terms.length) return true
  const own = searchTerms([issue.title, issue.sensor ?? '', issue.label, issue.detail, ...issue.projects].join(' ')).join(' ')
  return terms.every(term => own.includes(term) || (!!station && matchesStation(station, term)))
}

/** A strategy's alarm belongs to its own project, never to "no project". */
function inStrategyScope(own: string, project: string | null): boolean {
  return project === null || (project !== NO_PROJECT && own === project)
}

export function alarmsInScope(alarms: Alarm[], byCode: Map<string, Station>, project: string | null): Alarm[] {
  return alarms.filter(alarm => alarm.station ? inProjectScope(byCode.get(alarm.station.code), project)
    : inStrategyScope(alarm.project?.name ?? '', project))
}

/** The issues inside the project filter: what the cards count. The search
 *  narrows the queue under them, never the cards, so a count does not
 *  drop because of a word typed to find something else. */
export function issuesInScope(issues: OverviewIssue[], byCode: Map<string, Station>, project: string | null): OverviewIssue[] {
  return issues.filter(issue => issue.project !== undefined ? inStrategyScope(issue.project, project)
    : inProjectScope(issue.station ? byCode.get(issue.station) : undefined, project))
}

export function searchIssues(issues: OverviewIssue[], byCode: Map<string, Station>, query: string): OverviewIssue[] {
  return issues.filter(issue => issueMatches(issue, byCode.get(issue.station), query))
}

/** The queue's rows for the chosen card or the reader's flags, known ones
 *  last (orderIssues). */
export function issuesShown(issues: OverviewIssue[], filter: IssueFilter, flags: Set<string>): OverviewIssue[] {
  // A flagged issue is a known one (PM-19): a damaged sensor should not
  // appear in Needs attention every time. It leaves All issues for the
  // Flagged view, and returns when its flag stops holding.
  const kept = filter === 'all' ? issues.filter(issue => !flags.has(issue.id))
    : filter === 'flagged' ? issues.filter(issue => flags.has(issue.id))
      : issues.filter(issue => issue.category === filter)
  return orderIssues(kept, flags)
}

/** Why the queue is empty, most fundamental reason first: a list that
 *  failed to load is not a quiet network. */
export function queueEmptyText(state: { unavailable: boolean; filter: IssueFilter; scoped: number; narrowed: boolean }): string {
  if (state.unavailable) return 'Station status unavailable'
  if (state.filter === 'flagged') return 'No flagged issues'
  if (state.scoped) return 'No issues in this category'
  return state.narrowed ? 'No issues match this filter' : 'No current issues'
}

/** Where an issue's detail leads. The alarm evaluation itself has no
 *  station: whoever can set rules goes to them, anyone else gets plain
 *  text (null), never a link that would drop the page's filters. */
export function issueTarget(issue: OverviewIssue, canManageRules: boolean): string | null {
  return issue.category === 'checks' && !issue.station && canManageRules ? '/alerts' : issue.to
}

/** The stations with at least one issue in the queue: what the station
 *  list's attention switch keeps, so "needing attention" means the same on
 *  both. The alarm evaluation belongs to no station and adds none. */
export function stationsWithIssues(issues: OverviewIssue[]): Set<string> {
  return new Set(issues.map(issue => issue.station).filter(Boolean))
}

/** What the attention switch keeps: a known issue leaves it, as it leaves the
 *  queue, but an open alarm never does. Flagging an alarm is not suppressing it, as
 *  the Alarms card goes on saying, so its station still needs attention. */
export function stationsNeedingAttention(issues: OverviewIssue[], flags: ReadonlySet<string>): Set<string> {
  return stationsWithIssues(issues.filter(issue => issue.category === 'alarms' || !flags.has(issue.id)))
}

// ------------------------------------------------------- the station list

export interface StationView {
  /** The project filter's stations: what the attention switch counts. */
  inProject: Station[]
  /** Of those, the ones with an issue in the Needs Attention queue. */
  flagged: Station[]
  /** After the attention switch and the search: what the source counts count. */
  matching: Station[]
  /** After the source too: the rows. */
  shown: Station[]
  sourceCounts: Record<'all' | StationSource, number>
}

/** The station list's filters in the Stations board's order. The project
 *  filter first: every count below is of the stations it leaves, and none
 *  of their statuses change (lib/projects byProject). Search and attention
 *  next; the source counts are of what they leave, so a reader sees what
 *  picking a source would show before picking it.
 *
 *  `attention` is the stations with a new issue or an open alarm
 *  (stationsNeedingAttention). The Stations board asked the health monitor alone
 *  (lib/health needsAttention); the queue is wider (rule alarms, late readings,
 *  quiet sensors, stations whose monitoring is delayed), and one page must not give
 *  two answers to "what needs attention". Every station the old switch kept is still
 *  kept, so an old ?attention=1 link shows at least what it did, except one whose
 *  every issue someone has flagged as known. */
export function stationView(stations: Station[], filters: Pick<OverviewFilters, 'q' | 'attention' | 'source'>,
                            project: string | null, attention: ReadonlySet<string>): StationView {
  const inProject = byProject(stations, project)
  const flagged = inProject.filter(station => attention.has(station.code))
  const matching = filterStations(filters.attention ? flagged : inProject, filters.q)
  const shown = filters.source ? matching.filter(station => sourceOf(station) === filters.source) : matching
  const mrl = matching.filter(station => sourceOf(station) === 'mrl').length
  return { inProject, flagged, matching, shown, sourceCounts: { all: matching.length, mrl, ws: matching.length - mrl } }
}

// -------------------------------------------------------- what is current

/** The newest health check any station carries: the header's "Checked"
 *  time, and what the stopped-checks banner measures. */
export function newestCheck(stations: Pick<Station, 'health_checked_at'>[]): string | null {
  return stations.map(station => station.health_checked_at)
    .filter((at): at is string => !!at && Number.isFinite(Date.parse(at))).sort().at(-1) ?? null
}

/** The stopped-checks banner's line. A stopped check must never leave a
 *  station reading "normal": this says why the statuses are not current. */
export function healthChecksText(checkedAt: string | null): string {
  return checkedAt ? `The health checks have not run since ${formatDateTime(checkedAt)}.` : 'The health checks have not run yet.'
}

/** The line above everything when a poll fails. A refresh that failed keeps
 *  the last good records on screen and says so; a first load that failed
 *  has nothing to keep, and says what went wrong instead. */
export function refreshNotice(stations: { error?: string; loaded: boolean }, alarms: { error?: string; loaded: boolean }): string {
  const parts: string[] = []
  if (stations.error) parts.push(stations.loaded ? 'Station status could not refresh.' : `Stations could not load (${stations.error}).`)
  if (alarms.error) parts.push(alarms.loaded ? 'Alarm status could not refresh.' : `Alarms could not load (${alarms.error}).`)
  if ((stations.error && stations.loaded) || (alarms.error && alarms.loaded)) parts.push('Last available records are shown with their check times.')
  return parts.join(' ')
}

/** What a project filter names "outside": "NEA Landslides", or for the No
 *  project choice, "this filter". */
export function scopeLabel(project: string): string {
  return project === NO_PROJECT ? 'this filter' : project
}

/** The No data notice's sentence, as the Stations board worded it. */
export function hiddenNoDataText(names: string[], project: string): string {
  // A count, not every station by name (Tamar, 2026-10-28): Show all
  // projects beside it brings them into view.
  return `${names.length === 1 ? '1 station with no data is' : `${names.length} stations with no data are`} outside ${scopeLabel(project)}.`
}

/** Open alarms a project filter hides, said beside the No data stations it
 *  hides: the cards, the Alarms panel and the queue all follow the filter,
 *  so this line is what keeps them in sight. */
export function hiddenAlarmsText(alarms: Alarm[], project: string): string {
  if (!alarms.length) return ''
  const c = alarmCounts(alarms)
  const what = [c.alarms ? `${c.alarms} ${c.alarms === 1 ? 'alarm' : 'alarms'}` : '',
    c.warnings ? `${c.warnings} ${c.warnings === 1 ? 'warning' : 'warnings'}` : ''].filter(Boolean).join(' and ')
  return `${what} ${alarms.length === 1 ? 'is' : 'are'} open outside ${scopeLabel(project)}.`
}

// ------------------------------------------------------ the summary cards

/** "Zhinvali, Bodorna Dam and Kitskhi" */
export function listNames(names: string[]): string {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

export function plural(n: number): string {
  return `${n} ${n === 1 ? 'station' : 'stations'}`
}

/** The station list's count: "4 of 10 stations" while anything narrows it. */
export function stationCount(shown: number, total: number, filtered: boolean): string {
  return filtered ? `${shown} of ${plural(total)}` : plural(total)
}

export interface NetworkStatus { total: number; noData: Station[]; receiving: number; notChecked: number; alarming: boolean }

/** The System status card's figures, by each station's own status (one
 *  station's checks can lapse before another's). Transmission only: an
 *  alarm is a different signal and has its own card. */
export function networkStatus(stations: Station[], statusOf: (station: Station) => SystemStatus): NetworkStatus {
  const out: NetworkStatus = { total: stations.length, noData: [], receiving: 0, notChecked: 0, alarming: false }
  for (const station of stations) {
    const status = statusOf(station)
    if (status.status === 'receiving') out.receiving += 1
    else if (status.status === 'not_checked') out.notChecked += 1
    else {
      out.noData.push(station)
      // Alarm text only when a station the monitor calls silent is among
      // them: collection switched off fails nothing (section 2.6, stale grey).
      if (statusTone(status) === 'alarm') out.alarming = true
    }
  }
  return out
}

/** The Stations card's small line. Every station is in exactly one of
 *  receiving, no data or monitoring delayed, and the line says each part that
 *  is not receiving: stations whose monitoring is delayed must not hide the ones
 *  with no data beside them. */
export function stationsCardNote(n: NetworkStatus): string {
  if (!n.total) return 'No stations in this view.'
  const parts = [
    n.noData.length ? `${n.noData.length} not receiving` : '',
    n.notChecked ? `${n.notChecked} monitoring delayed` : '',
  ].filter(Boolean)
  return parts.length ? parts.join(', ') : 'All stations receiving'
}

export interface AlarmSummary { value: string; word: boolean; note: string }

/** The Alarms and warnings card: the Stations board's Alarms card in the
 *  Overview's row. With the rule monitor quiet it says the rules are not
 *  being checked, never "no alarm"; alarms known from before stay counted.
 *
 *  `current`: the figures are this minute's (the monitor ran and the last
 *  request answered). `checked`: the monitor itself ran recently, by its own
 *  checked_at. A refresh that failed in this browser is not the monitor's
 *  fault: the card keeps the last known figures and leaves the reason to the
 *  notice above the cards, which says the alarm status could not refresh. */
export function alarmSummary(alarms: Alarm[] | undefined,
                             state: { current: boolean; checked: boolean; loading: boolean; error?: string; outside: number }): AlarmSummary {
  if (!alarms) {
    if (state.loading && !state.error) return { value: '…', word: false, note: 'Loading alarms' }
    return { value: 'N/A', word: false, note: state.error ? `Alarms could not load: ${state.error}` : 'Awaiting current evaluation' }
  }
  const outside = state.outside ? ` ${state.outside} more outside this project filter.` : ''
  const quiet = 'The alert rules are not being checked right now.'
  if (!state.checked && !alarms.length) return { value: MONITORING_DELAYED, word: true, note: `${quiet}${outside}` }
  const c = alarmCounts(alarms)
  const counts = alarms.length
    ? alarmCountsText(c)
    : 'No alarm is active.'
  return { value: String(alarms.length), word: false,
    note: state.current ? `${counts}${outside}` : `Last known: ${counts}${state.checked ? '' : ` ${quiet}`}${outside}` }
}

// --------------------------------------------------------- the station list

/** The word beside a Last Observation colour (a status colour always sits
 *  beside a word). Overdue is the monitor's silent threshold passed, which
 *  the System Status column calls No data. */
export const FRESHNESS_WORD: Record<Freshness, string> = { ok: 'On time', late: 'Late', over: 'Overdue', never: 'Never read' }

/** The legend under the station list: each colour by the word its cells
 *  print, then what that means, so one state goes by one name on a row. */
export const FRESHNESS_LEGEND: { fresh: Exclude<Freshness, 'never'>; text: string }[] = [
  { fresh: 'ok', text: FRESHNESS_WORD.ok },
  { fresh: 'late', text: `${FRESHNESS_WORD.late}: latest expected reading missing` },
  { fresh: 'over', text: `${FRESHNESS_WORD.over}: no data` },
]

/** The cell's title, in the monitor's own terms: its expected interval,
 *  and the threshold past which it calls the station silent. */
export function freshTitle(fresh: Freshness, every: string, silentAfter: string): string {
  switch (fresh) {
    case 'ok': return `On time, expected every ${every}`
    case 'late': return `The latest reading, expected every ${every}, has not arrived`
    case 'over': return `Nothing new for over ${silentAfter}: no data`
    case 'never': return 'Never read'
  }
}

export function stationsEmptyMessage(query: string, attention: boolean, source: StationSource | null): string {
  const from = source ? ` from ${SOURCE_LABEL[source]}` : ''
  if (query.trim()) {
    return `No station matches “${query.trim()}”${attention ? ' among those needing attention' : ''}${from}.`
  }
  return attention ? `No station${from} needs attention.` : `No station${from}.`
}

// ----------------------------------------------------------------- layout

/** Which sections a reader keeps on their Overview, saved to their account (db/035). */
export interface OverviewLayout { summary: boolean; attention: boolean; alarms: boolean; registry: boolean }
export const DEFAULT_OVERVIEW_LAYOUT: OverviewLayout = { summary: true, attention: true, alarms: true, registry: true }
export const OVERVIEW_LAYOUT_KEY = 'hydro.overview-layout.v1'

/** Read key by key: a layout saved before a section existed (or with the
 *  retired chart height) keeps the reader's other choices, and the new
 *  section shows. Anything unreadable is the complete overview. */
export function parseOverviewLayout(raw: string | null): OverviewLayout {
  let value: unknown
  try { value = JSON.parse(raw ?? 'null') } catch { value = null }
  const saved = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
  const layout = { ...DEFAULT_OVERVIEW_LAYOUT }
  for (const key of Object.keys(layout) as (keyof OverviewLayout)[]) {
    if (typeof saved[key] === 'boolean') layout[key] = saved[key]
  }
  return layout
}
